import { memo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserPlus, X, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { FriendSuggestion } from "@/utils/friendSuggestions";
import { PremiumHatOverlay } from "@/components/profile/PremiumHatOverlay";
import { PremiumBadge } from "@/components/profile/PremiumBadge";
import { LegendaryGlow } from "@/components/profile/LegendaryGlow";

interface SuggestionCardProps {
  suggestion: FriendSuggestion;
  onAddFriend: (userId: string) => void;
  onDismiss?: (userId: string) => void;
  isAdding?: boolean;
  variant?: "list" | "carousel";
  'data-tour-id'?: string;
}

export const SuggestionCard = memo(function SuggestionCard({ 
  suggestion, 
  onAddFriend, 
  onDismiss,
  isAdding,
  variant = "list",
  'data-tour-id': dataTourId
}: SuggestionCardProps) {
  const navigate = useNavigate();

  return (
    <Card className="p-4 hover:bg-accent/50 transition-colors relative" data-tour-id={dataTourId}>
      {variant === "carousel" && onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(suggestion.userId);
          }}
          className="absolute top-2 right-2 h-6 w-6 p-0 rounded-full hover:bg-destructive/10"
        >
          <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
        </Button>
      )}
        <div className="flex items-center gap-3">
          <LegendaryGlow isLegendary={(suggestion as any).event_count >= 100} size="lg">
            <div className="relative">
              <Avatar 
                className="w-12 h-12 cursor-pointer"
                onClick={() => navigate(`/profile/${suggestion.userId}`)}
              >
                <AvatarImage src={suggestion.avatar_url || ""} />
                <AvatarFallback>
                  {suggestion.username?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <PremiumHatOverlay 
                isPremium={suggestion.subscription_tier === 'premium' && suggestion.subscription_status === 'active'}
                isFoundingMember={suggestion.is_founding_member}
                size="sm"
              />
            </div>
          </LegendaryGlow>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h3 
              className="font-semibold truncate cursor-pointer hover:text-primary"
              onClick={() => navigate(`/profile/${suggestion.userId}`)}
            >
              {suggestion.full_name}
            </h3>
            {(suggestion.subscription_tier === 'premium' && suggestion.subscription_status === 'active') && (
              <PremiumBadge isFoundingMember={suggestion.is_founding_member} size="sm" />
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            @{suggestion.username}
          </p>
          
          {/* Mutual Friends Indicator */}
          {suggestion.mutualFriends > 0 && (
            <div className="flex items-center gap-1 mt-2 mb-1">
              <Users className="w-3.5 h-3.5 text-primary" />
              <span className="text-sm font-medium text-primary">
                {suggestion.mutualFriends} mutual friend{suggestion.mutualFriends > 1 ? 's' : ''}
              </span>
            </div>
          )}
          
          {/* Other connection reasons */}
          {(suggestion.sharedEvents > 0 || suggestion.sharedHerds > 0) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {suggestion.sharedEvents > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {suggestion.sharedEvents} shared event{suggestion.sharedEvents > 1 ? 's' : ''}
                </Badge>
              )}
              {suggestion.sharedHerds > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {suggestion.sharedHerds} shared herd{suggestion.sharedHerds > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          )}
        </div>

        <Button
          size="sm"
          onClick={() => onAddFriend(suggestion.userId)}
          disabled={isAdding}
          className="shrink-0"
          data-tour-id="add-friend-button"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          Add Friend
        </Button>
      </div>
    </Card>
  );
});
